/* eslint-disable react-hooks/exhaustive-deps */
import { useSelector } from "react-redux";
import { useMemo, useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import { useUserConfig } from "app/hook";
import { RestrictedFeatureSource } from "app/types";
import { FadeTransition } from "common/elements";
import { useScale, btnDelay, usePlaySound, shouldApplyGuestRestrictions } from "common/utils";
import { AccountType } from "features/user/types";
import { ProjectType } from "features/creator/types";
import Stages from "features/creator/tutorial/top/stages";
import { selectAllProjects } from "features/creator/slice";
import { ProjectLimitDialog } from "features/creator/elements";
import { TutorialHint } from "features/creator/tutorial/top/TutorialHint";
import {
  usePlan,
  usePlanLimits,
  PlanType,
  PLAN_LIMITS,
  useEnforcePlanLimit,
} from "features/subscription";

export const CreatorTopTutorial = ({
  isTutorialApp,
  showTutorial,
  tutorialStage,
  setShowTutorial,
}: {
  isTutorialApp: boolean;
  showTutorial: boolean;
  tutorialStage: number;
  setShowTutorial: (show: boolean) => void;
}) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const plan = usePlan();
  const limits = usePlanLimits();
  const enforcePlanLimit = useEnforcePlanLimit();
  const [hint, setHint] = useState(false);
  const [show, setShow] = useState(false);
  const handleChangeUserConfig = useUserConfig();
  const [guidance, setGuidance] = useState(false);
  const projects = useSelector(selectAllProjects);
  const [step, setStep] = useState<number | null>(null);
  const [stage, setStage] = useState<number | null>(null);
  const tutorial = useSelector((state: RootState) => state.config.userConfig.tutorial.creator_top);
  const active = useSelector((state: RootState) => state.user.active);
  const userProjects = useMemo(
    () =>
      projects.filter(
        (project) =>
          project.author_uid === active.uid &&
          !project.deleted_at &&
          project.type === ProjectType.BASIC,
      ),
    [projects, active.uid],
  );

  useEffect(() => {
    if (isTutorialApp) {
      setStage(4);
      setStep(20);
      setShow(true);
    }
  }, [isTutorialApp]);

  useEffect(() => {
    if (tutorial) {
      setShow(true);
    }
  }, [tutorial]);

  useEffect(() => {
    if (showTutorial) {
      // OLD TRIALユーザー判定（2025-11-02以前に作成されたGUESTユーザー）
      const isOldTrialUser =
        plan === PlanType.TRIAL &&
        active.account_type === AccountType.GUEST &&
        !shouldApplyGuestRestrictions(active.created_at);

      // 制限値を計算（OLD TRIALはFREEと同じ制限）
      const projectLimit = isOldTrialUser
        ? PLAN_LIMITS[PlanType.FREE].projects.limit
        : limits.projects.limit;

      if (
        (tutorialStage === 1 || tutorialStage === 3 || tutorialStage === 4) &&
        !limits.projects.unlimited &&
        userProjects.length >= projectLimit
      ) {
        // TRIAL は新 Dialog、FREE/STANDARD は RestrictedFeatureDialog
        if (plan === PlanType.TRIAL || plan === PlanType.FREE) {
          setShowTutorial(false);
          enforcePlanLimit(RestrictedFeatureSource.PROJECT_LIMIT);
          return;
        }
        // EDUCATION/PREMIUM/BUILDER: ProjectLimitDialog表示（通知のみ）
        setGuidance(true);
      } else {
        setStep(0);
        setStage(tutorialStage);
        setShow(true);
      }
    }
  }, [showTutorial]);

  useEffect(() => {
    // OLD TRIALユーザー判定（2025-11-02以前に作成されたGUESTユーザー）
    const isOldTrialUser =
      plan === PlanType.TRIAL &&
      active.account_type === AccountType.GUEST &&
      !shouldApplyGuestRestrictions(active.created_at);

    // 制限値を計算（OLD TRIALはFREEと同じ制限）
    const projectLimit = isOldTrialUser
      ? PLAN_LIMITS[PlanType.FREE].projects.limit
      : limits.projects.limit;

    if (
      (stage === 1 || stage === 3 || stage === 4) &&
      !limits.projects.unlimited &&
      userProjects.length >= projectLimit
    ) {
      setShow(false);
      // TRIAL は新 Dialog、FREE/STANDARD は RestrictedFeatureDialog
      if (plan === PlanType.TRIAL || plan === PlanType.FREE) {
        enforcePlanLimit(RestrictedFeatureSource.PROJECT_LIMIT);
        return;
      }
      // EDUCATION/PREMIUM/BUILDER: ProjectLimitDialog表示（通知のみ）
      setGuidance(true);
    }
  }, [stage]);

  const handleBackFromHint = () => {
    play();
    btnDelay(() => {
      setHint(false);
      setShow(true);
    });
  };
  const handleCloseHint = () => {
    play();
    btnDelay(() => {
      setHint(false);
      setStep(null);
      setStage(null);
    });
  };

  const handleBack = () => {
    play();
    btnDelay(() => {
      setStep(step - 1);
    });
  };

  const handleNext = () => {
    play();
    btnDelay(() => {
      if (step === Stages[stage].length - 1) {
        if (stage === Stages.length - 1) {
          setStep(null);
          setStage(null);
          setShow(false);
          handleChangeUserConfig("tutorial.creator_top", false, false);
        } else {
          setStep(0);
          setStage(stage + 1);
        }
      } else {
        setStep(step + 1);
      }
    });
  };

  const handleClose = (nohint?: boolean) => {
    play();
    setHint(!nohint);
    setShow(false);
    handleChangeUserConfig("tutorial.creator_top", false, false);
  };

  const handleExited = () => {
    if (showTutorial) {
      setShowTutorial(false);
      return;
    }
    setHint(true);
  };

  const handleCloseGuidance = () => {
    play();
    setGuidance(false);
    setShowTutorial(false);
  };

  // プラン別ダイアログのアクションハンドラ
  const handleGuidanceAction = () => {
    play();
    setGuidance(false);
    setShowTutorial(false);
    btnDelay(() => {
      if (plan === PlanType.TRIAL || plan === PlanType.FREE) {
        // TRIAL は新 Dialog、FREE は RestrictedFeatureDialog
        enforcePlanLimit(RestrictedFeatureSource.PROJECT_LIMIT);
      }
      // EDUCATION, PREMIUM, BUILDERはボタンがないのでこの関数は呼ばれない
    });
  };

  if (guidance)
    return <ProjectLimitDialog open={guidance} type="tutorial" close={handleCloseGuidance} />;

  if (stage === null || step === null) return <></>;

  const Contents = Stages[stage][step];
  return (
    <>
      {hint && <TutorialHint back={handleBackFromHint} close={handleCloseHint} />}

      <Dialog
        id="tutorial-dialog"
        fullScreen
        open={show}
        maxWidth={false}
        sx={{ zIndex: 10000 }}
        TransitionComponent={FadeTransition}
        componentsProps={{
          backdrop: {
            style: { backgroundColor: "transparent", pointerEvents: "none" },
          },
        }}
        PaperProps={{
          style: {
            overflow: "visible",
            margin: 0,
            maxWidth: "none",
            maxHeight: "none",
            transform: `scale(${scale})`,
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
        transitionDuration={{ enter: 1000, exit: 500 }}
        TransitionProps={{
          onExited: handleExited,
        }}
      >
        <div className="flex-col-center w-full h-full">
          <Contents back={handleBack} next={handleNext} close={handleClose} />
        </div>
      </Dialog>
    </>
  );
};
